/**
 * plugins/badge.ts — Badge individual (span.badge)
 *
 * Ferramentas: renomear texto, alterar cores bg/fg, mover ←→, excluir.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, rgbToHex, closestInside } from '../types';

export class BadgePlugin implements VtbPlugin {
    readonly panelId = 'vtb-badge';
    readonly selectorHints = ['span.badge', '.badge', '[data-vtb-id="badge"]'];
    readonly contextAffinity = ['vtb-cover', 'vtb-badges-group', 'vtb-doc-header', 'vtb-titles'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            e.classList.contains('badge') || e.dataset.vtbId === 'badge'
        );
        if (!el) return null;
        const container = el.parentElement?.classList.contains('badges') || el.parentElement?.dataset.vtbId === 'badges'
            ? (el.parentElement as HTMLElement)
            : undefined;
        return { type: this.panelId, label: 'Badge', el, container };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;
        const textIn = registry.q<HTMLInputElement>('vtb-badge-text');
        const bgIn   = registry.q<HTMLInputElement>('vtb-badge-bg');
        const fgIn   = registry.q<HTMLInputElement>('vtb-badge-fg');

        if (textIn) textIn.value = el.textContent?.trim() ?? '';
        if (bgIn)   bgIn.value   = rgbToHex(el.style.backgroundColor) || '#e8f5e9';
        if (fgIn)   fgIn.value   = rgbToHex(el.style.color) || '#2e7d32';
    }

    setup(registry: IVtbRegistry): void {
        const on = (id: string, fn: () => void) =>
            registry.q(id)?.addEventListener('click', fn);

        registry.q('vtb-badge-apply')?.addEventListener('click', () => this._apply(registry));
        registry.q<HTMLInputElement>('vtb-badge-bg')?.addEventListener('input', () => this._liveColors(registry));
        registry.q<HTMLInputElement>('vtb-badge-fg')?.addEventListener('input', () => this._liveColors(registry));
        on('vtb-badge-movel', () => this._move(registry, -1));
        on('vtb-badge-mover', () => this._move(registry, 1));
        on('vtb-badge-del',   () => this._delete(registry));
    }

    private _apply(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const text = registry.q<HTMLInputElement>('vtb-badge-text')?.value?.trim();
        if (text) ctx.el.textContent = text;
        this._liveColors(registry);
    }

    private _liveColors(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el = ctx.el;
        const bg = registry.q<HTMLInputElement>('vtb-badge-bg')?.value;
        const fg = registry.q<HTMLInputElement>('vtb-badge-fg')?.value;
        if (bg) { el.style.background = bg; el.style.backgroundColor = bg; }
        if (fg) el.style.color = fg;
        // Remove classes preset ao aplicar cores custom
        if (bg || fg) el.className = 'badge';
        registry.sync();
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el = ctx.el;
        const parent = el.parentElement;
        if (!parent) return;
        if (dir === -1) {
            const prev = el.previousElementSibling;
            if (prev) parent.insertBefore(el, prev);
        } else {
            const next = el.nextElementSibling;
            if (next) parent.insertBefore(next, el);
        }
        registry.sync();
    }

    private _delete(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.remove();
        registry.clearSelection();
        registry.sync();
    }
}
